import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Stethoscope, Loader2, AlertCircle, CheckCircle, ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [resetToken, setResetToken] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await authAPI.requestPasswordReset(email);
            setSuccess(true);
            // For testing purposes, store the token (remove in production with real email)
            if (response.data.reset_token) {
                setResetToken(response.data.reset_token);
            }
            toast.success('Reset instructions sent!');
        } catch (err) {
            const message = err.response?.data?.detail || 'Failed to process request. Please try again.';
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="flex items-center justify-center gap-2 mb-8">
                    <Stethoscope className="h-10 w-10 text-indigo-600" />
                    <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                        MedPrep NBME & CBSE
                    </span>
                </div>

                <Card className="border-slate-200 shadow-lg">
                    <CardHeader className="space-y-1">
                        <CardTitle className="text-2xl font-bold text-center" style={{ fontFamily: 'Manrope' }}>
                            Reset Password
                        </CardTitle>
                        <CardDescription className="text-center">
                            {success 
                                ? "Check your email for reset instructions"
                                : "Enter your email to receive a password reset link"
                            }
                        </CardDescription>
                    </CardHeader>

                    {success ? (
                        <CardContent className="space-y-4">
                            <div className="flex flex-col items-center gap-4 py-4">
                                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                                    <CheckCircle className="h-8 w-8 text-emerald-600" />
                                </div>
                                <p className="text-center text-slate-600">
                                    If an account exists for <strong>{email}</strong>, you will receive password reset instructions.
                                </p>
                                
                                {/* For testing - shows reset link (remove in production) */}
                                {resetToken && (
                                    <div className="w-full p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                        <p className="text-sm text-amber-800 mb-2">
                                            <strong>Testing Mode:</strong> Use this link to reset your password:
                                        </p>
                                        <Link 
                                            to={`/reset-password/${resetToken}`}
                                            className="text-sm text-indigo-600 hover:underline break-all"
                                        >
                                            Click here to reset password
                                        </Link>
                                    </div>
                                )}
                            </div>
                            <Button asChild className="w-full">
                                <Link to="/login">
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Back to Login
                                </Link>
                            </Button>
                        </CardContent>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <CardContent className="space-y-4">
                                {error && (
                                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                        {error}
                                    </div>
                                )}
                                
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email Address</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="you@example.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            className="pl-10"
                                            data-testid="forgot-email-input"
                                        />
                                    </div>
                                </div>
                            </CardContent>
                            <CardFooter className="flex flex-col gap-4">
                                <Button 
                                    type="submit" 
                                    className="w-full" 
                                    disabled={loading}
                                    data-testid="forgot-submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        'Send Reset Link'
                                    )}
                                </Button>
                                <p className="text-sm text-slate-600 text-center">
                                    Remember your password?{' '}
                                    <Link to="/login" className="text-indigo-600 hover:underline font-medium">
                                        Sign in
                                    </Link>
                                </p>
                            </CardFooter>
                        </form>
                    )}
                </Card>

                <p className="mt-6 text-center text-sm text-slate-500">
                    <Link to="/" className="hover:text-indigo-600">
                        ← Back to home
                    </Link>
                </p>
            </div>
        </div>
    );
}
